// Which voice the spoken agent speaks with: the platform's list, a sample of
// each, and one chosen for every device (the choice lives on the server, and
// the agent is re-made with it). The sample is fetched through the server and
// played from a blob URL, since the sample's host is outside the page's CSP.
import { useEffect, useRef, useState } from 'react'
import { Check, Pause, Play } from 'lucide-react'
import { api } from '@/runtime'
import type { AgentVoice } from '@/db/types'
import { Button, cx } from './ui'

export const VoicePicker = () => {
  const [voices, setVoices] = useState<AgentVoice[] | null>(null)
  const [current, setCurrent] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [playing, setPlaying] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const audio = useRef<HTMLAudioElement | null>(null)
  useEffect(() => {
    let stale = false
    api
      .voiceVoices()
      .then((r) => {
        if (stale) return
        setVoices(r.voices)
        setCurrent(r.current)
      })
      .catch((e: Error) => {
        if (!stale) setError(e.message)
      })
    return () => {
      stale = true
      audio.current?.pause()
    }
  }, [])
  const stop = () => {
    audio.current?.pause()
    audio.current = null
    setPlaying(null)
  }
  const preview = async (id: string) => {
    if (playing === id) {
      stop()
      return
    }
    stop()
    try {
      const blob = await api.voicePreview(id)
      const url = URL.createObjectURL(blob)
      const a = new Audio(url)
      audio.current = a
      setPlaying(id)
      a.onended = () => {
        URL.revokeObjectURL(url)
        setPlaying((p) => (p === id ? null : p))
      }
      await a.play()
    } catch (e) {
      setPlaying(null)
      setError(e instanceof Error ? e.message : 'Could not play the sample')
    }
  }
  const choose = async (id: string) => {
    setSaving(id)
    setError(null)
    try {
      const r = await api.voiceSetVoice(id)
      if (r.error) throw new Error(r.error)
      setCurrent(id)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the voice')
    } finally {
      setSaving(null)
    }
  }
  if (error && !voices) return <p className="text-sm text-amber-300/90">{error}</p>
  if (!voices) return <p className="text-sm text-faint">Loading voices…</p>
  const needle = q.trim().toLowerCase()
  const shown = voices.filter(
    (v) =>
      !needle ||
      v.name.toLowerCase().includes(needle) ||
      Object.values(v.labels ?? {}).some((l) => l.toLowerCase().includes(needle)) ||
      (v.description ?? '').toLowerCase().includes(needle),
  )
  const currentVoice = voices.find((v) => v.voice_id === current)
  return (
    <div className="flex flex-col gap-3" data-testid="voice-picker">
      <p className={cx('text-faint', 'text-xs')}>
        Speaking as <span className="text-fg">{currentVoice?.name ?? "the platform's default"}</span>. The
        choice applies on every device.
      </p>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Filter by name, accent, age…"
        className="w-full"
        data-testid="voice-picker-filter"
      />
      {error && <p className="text-sm text-amber-300/90">{error}</p>}
      <ul className="flex flex-col divide-y divide-line rounded-[var(--radius-card-inner)] border border-line overflow-hidden max-h-[420px] overflow-y-auto">
        {shown.map((v) => {
          // the platform's tags as one sentence-case phrase ("British, female"), never dot-joined
          const tags = Object.entries(v.labels ?? {})
            .filter(([k]) => ['accent', 'gender', 'age', 'use_case', 'descriptive'].includes(k))
            .map(([, val]) => val.replace(/_/g, ' '))
            .join(', ')
          const labels = tags.charAt(0).toUpperCase() + tags.slice(1)
          const on = v.voice_id === current
          return (
            <li
              key={v.voice_id}
              className={cx('flex items-center gap-3 px-3', 'py-2', on && 'bg-accent-soft')}
              data-testid={`voice-${v.voice_id}`}
            >
              <button
                type="button"
                onClick={() => void preview(v.voice_id)}
                disabled={!v.preview_url}
                aria-label={playing === v.voice_id ? `Stop ${v.name}` : `Play a sample of ${v.name}`}
                className={cx(
                  'shrink-0 rounded-full flex items-center justify-center bg-raised disabled:opacity-40',
                  'w-8 h-8',
                )}
              >
                {playing === v.voice_id ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
              </button>
              <span className="min-w-0 flex-1">
                <span className={cx('block truncate', 'text-sm')}>{v.name}</span>
                {labels && <span className={cx('block text-faint truncate', 'text-xs')}>{labels}</span>}
              </span>
              {on ? (
                <span className="inline-flex items-center gap-1 text-accent-text text-xs shrink-0">
                  <Check size={14} /> Chosen
                </span>
              ) : (
                <Button variant="chip" onClick={() => void choose(v.voice_id)} disabled={saving !== null}>
                  {saving === v.voice_id ? 'Saving…' : 'Use'}
                </Button>
              )}
            </li>
          )
        })}
        {shown.length === 0 && <li className="px-3 py-3 text-sm text-faint">Nothing matches.</li>}
      </ul>
    </div>
  )
}
